import React, { useState, useEffect } from 'react';
import { gdprAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Shield, Download, Trash2, Settings, Clock, Database, AlertTriangle } from 'lucide-react';

export default function AdminGDPR() {
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [retentionPolicies, setRetentionPolicies] = useState([]);
  const [consent, setConsent] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('requests');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [reqRes, polRes, conRes] = await Promise.allSettled([
        gdprAPI.listDeletionRequests(),
        gdprAPI.retentionPolicies(),
        gdprAPI.getConsent(),
      ]);
      if (reqRes.status === 'fulfilled') setDeletionRequests(reqRes.value.data.requests || []);
      if (polRes.status === 'fulfilled') setRetentionPolicies(polRes.value.data.policies || []);
      if (conRes.status === 'fulfilled') setConsent(conRes.value.data);
    } catch {}
    finally { setLoading(false); }
  };

  const exportData = async () => {
    try {
      const res = await gdprAPI.exportData();
      const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'user-data-export.json';
      a.click();
      toast.success('Data exported successfully');
    } catch { toast.error('Failed to export data'); }
  };

  const updateConsent = async (key, value) => {
    try {
      const newConsent = { ...consent, [key]: value };
      await gdprAPI.updateConsent(newConsent);
      setConsent(newConsent);
      toast.success('Consent preferences updated');
    } catch { toast.error('Failed to update consent'); }
  };

  const tabs = [
    { id: 'requests', label: 'Deletion Requests', icon: Trash2 },
    { id: 'retention', label: 'Data Retention', icon: Database },
    { id: 'consent', label: 'Consent Settings', icon: Settings },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">GDPR & Data Protection</h1>
          <p className="text-sm text-[#5B616E] mt-1">Manage data protection compliance, deletion requests, and retention policies</p>
        </div>
        <Button onClick={exportData} className="bg-[#1F7A5A] hover:bg-[#1F7A5A]/90">
          <Download className="w-4 h-4 mr-2" /> Export My Data
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-[#E5DFD5] p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center"><Trash2 className="w-4 h-4 text-red-500" /></div>
            <span className="text-xs text-[#5B616E]">Pending Deletions</span>
          </div>
          <p className="text-2xl font-bold text-[#1A1A1A]">{deletionRequests.filter(r => r.status === 'pending').length}</p>
        </div>
        <div className="bg-white rounded-xl border border-[#E5DFD5] p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Database className="w-4 h-4 text-blue-500" /></div>
            <span className="text-xs text-[#5B616E]">Retention Policies</span>
          </div>
          <p className="text-2xl font-bold text-[#1A1A1A]">{retentionPolicies.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-[#E5DFD5] p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center"><Shield className="w-4 h-4 text-green-500" /></div>
            <span className="text-xs text-[#5B616E]">Compliance Status</span>
          </div>
          <p className="text-2xl font-bold text-green-600">Active</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#F5F0E8] rounded-lg p-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === t.id ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#5B616E]'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'requests' && (
        <div className="bg-white rounded-xl border border-[#E5DFD5] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F0E8] border-b border-[#E5DFD5]">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">User</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Reason</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Status</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Requested</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5DFD5]">
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-[#5B616E]">Loading...</td></tr>
              ) : deletionRequests.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-[#5B616E]">No deletion requests.</td></tr>
              ) : deletionRequests.map(r => (
                <tr key={r.id} className="hover:bg-[#FBF7EF]">
                  <td className="px-4 py-3 font-mono text-xs">{r.user_id}</td>
                  <td className="px-4 py-3">{r.reason || 'No reason given'}</td>
                  <td className="px-4 py-3"><Badge className={r.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}>{r.status}</Badge></td>
                  <td className="px-4 py-3 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'retention' && (
        <div className="bg-white rounded-xl border border-[#E5DFD5] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F0E8] border-b border-[#E5DFD5]">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Data Type</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Retention Period</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Auto-Purge</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5DFD5]">
              {retentionPolicies.map((p, i) => (
                <tr key={i} className="hover:bg-[#FBF7EF]">
                  <td className="px-4 py-3 font-medium capitalize">{p.data_type?.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3 flex items-center gap-2"><Clock className="w-4 h-4 text-[#5B616E]" />{Math.round(p.retention_days / 365)} year{Math.round(p.retention_days / 365) !== 1 ? 's' : ''}</td>
                  <td className="px-4 py-3">{p.auto_purge ? <Badge className="bg-green-100 text-green-700">Enabled</Badge> : <Badge className="bg-gray-100 text-gray-600">Disabled</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'consent' && (
        <div className="bg-white rounded-xl border border-[#E5DFD5] p-6 space-y-4">
          <div className="flex items-start gap-3 p-4 rounded-lg border border-[#E5DFD5]">
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Consent preferences apply to your own account</p>
              <p className="text-xs text-[#5B616E]">These settings control what data processing you consent to.</p>
            </div>
          </div>
          {[
            { key: 'analytics', label: 'Analytics Tracking', desc: 'Allow usage analytics to improve the platform' },
            { key: 'marketing', label: 'Marketing Communications', desc: 'Receive promotional emails and newsletters' },
            { key: 'personalization', label: 'Content Personalization', desc: 'Use reading history for article recommendations' },
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between p-4 rounded-lg border border-[#E5DFD5]">
              <div>
                <p className="text-sm font-medium">{item.label}</p>
                <p className="text-xs text-[#5B616E]">{item.desc}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={consent[item.key] ?? false} onChange={e => updateConsent(item.key, e.target.checked)} className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1E5A8A]"></div>
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
